import { inject, Injectable } from '@angular/core';
import { OUTLAW_COMMUNITY_ROUTES } from '../../providers/community-route.provider';
import { CommunityConfigService } from '../util';
import { Router } from '@angular/router';
import { OUTLAW_QUERY_PARAMS, TargetUrl } from '@outlaw/core';
import { FeedFacade } from '../../store/feed';
import { CommunityTabType } from '../../interfaces';

export const POST_NAVIGATION_CONTEXT_KEY = 'post-navigation-context';
export type NavigationContext = {
  returnUrl: string;
  postId: string;
  tabId?: string;
  isMajiApp?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class CommunityNavigationService {
  #communityRoutes = inject(OUTLAW_COMMUNITY_ROUTES);
  #router = inject(Router);
  #communityConfigService = inject(CommunityConfigService);
  #feedFacade = inject(FeedFacade);

  get baseUrl(): string {
    return window.location.origin;
  }

  getPostUrl(
    postId: string,
    options: {
      includeBaseUrl: boolean;
    } = {
      includeBaseUrl: true,
    }
  ): string {
    return `${options.includeBaseUrl ? this.baseUrl : ''}/${this.#communityRoutes.post}?${OUTLAW_QUERY_PARAMS.postId}=${postId}`;
  }

  getSubscriptionUrl(queryParams: Record<string, string>): string {
    const url = new URL(`${this.baseUrl}/${this.#communityRoutes.subscribe}`);

    Object.entries(queryParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    return url.toString();
  }

  getProfileUrl(): string {
    return `${this.baseUrl}/${this.#communityRoutes.profile}/`;
  }

  navigateToPost(
    postId: string,
    target: TargetUrl = TargetUrl.newWindow,
    contextData?: { returnUrl?: string; tabId?: string; isMajiApp?: boolean }
  ): void {
    if (target === TargetUrl.sameWindow && contextData) {
      // Save context for scroll restoration
      this.setNavigationContext({
        returnUrl: contextData.returnUrl || this.#router.url,
        postId,
        tabId: contextData.tabId,
        isMajiApp: contextData.isMajiApp,
      });
      const navigationPath = `/${this.#communityRoutes.post}`;
      this.#router.navigate([navigationPath], {
        queryParams: { [OUTLAW_QUERY_PARAMS.postId]: postId },
      });
    } else {
      window.open(this.getPostUrl(postId), target);
    }
  }

  private navigationContext: NavigationContext | null = null;

  setNavigationContext(context: { returnUrl: string; postId: string; tabId?: string; isMajiApp?: boolean }): void {
    this.navigationContext = context;
    // Also save to sessionStorage for persistence across page reloads
    sessionStorage.setItem(POST_NAVIGATION_CONTEXT_KEY, JSON.stringify(context));
  }

  getNavigationContext(): NavigationContext | null {
    if (this.navigationContext) {
      return this.navigationContext;
    }
    const stored = sessionStorage.getItem(POST_NAVIGATION_CONTEXT_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  clearNavigationContext(): void {
    this.navigationContext = null;
    sessionStorage.removeItem(POST_NAVIGATION_CONTEXT_KEY);
  }

  navigateToPostFromProfile(
    postId: string,
    target: TargetUrl = TargetUrl.sameWindow,
    tabId?: CommunityTabType,
    returnUrl?: string,
    isMajiApp?: boolean
  ): void {
    if (tabId) {
      this.#feedFacade.setLastViewedPost(tabId, postId);
      this.#feedFacade.setLastActiveTab(tabId);
    }

    this.navigateToPost(postId, target, {
      returnUrl,
      tabId,
      isMajiApp,
    });
  }

  returnToPreviousContext(): boolean {
    const context = this.getNavigationContext();
    if (!context) {
      return false;
    }

    // Set target post for scroll restoration
    // Use tabId from context or fallback to lastActiveTab
    const tabId = context.tabId || this.#feedFacade.selectLastActiveTab()();
    if (tabId && context.postId) {
      this.#feedFacade.setTargetPost(tabId as CommunityTabType, context.postId);
    }

    this.#router.navigate([context.returnUrl]);
    this.clearNavigationContext();
    return true;
  }

  navigateToHome(): void {
    this.#router.navigate([`/${this.#communityRoutes.home}`]);
  }

  navigateToAdminPostEdit(postId: string, target: TargetUrl = TargetUrl.newWindow): void {
    const creatorLink = this.#communityConfigService.getConfig().links.creatorLink;

    const communityAdminLink = `${creatorLink}/${this.#communityRoutes.editPost}?id=${postId}`;
    window.open(communityAdminLink, target);
  }

  openExternalUrl(url: string, target: TargetUrl = TargetUrl.newWindow): void {
    window.open(url, target);
  }

  navigateToSubscription(queryParams: Record<string, string>, target: TargetUrl = TargetUrl.newWindow): void {
    window.open(this.getSubscriptionUrl(queryParams), target);
  }
}
